import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card } from '../ui';
import { ExclamationTriangleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

export const PROCESS_TYPE_LABELS = {
  BASIC_INFO: 'Basic Info',
  GROUP_JOIN: 'Group Join',
  JOIN_GROUPS: 'Group Join',
  AI_ANALYSIS: 'AI Analysis',
  ACCOUNT_SCAN: 'Account Scan',
  UNSUBSCRIBE: 'Unsubscribe',
  ADD_GROUPS: 'Add Groups',
};

export const LIVE_STATUS_VARIANTS = {
  online: 'green',
  checking: 'yellow',
  offline: 'red',
  error: 'red',
  unknown: 'gray',
};

export const PROCESS_STATUS_VARIANTS = {
  in_progress: 'blue',
  pending: 'yellow',
  paused: 'yellow',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
};

export const formatTimeAgo = (value) => {
  if (!value) return 'unknown';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export const StatTile = ({ title, value, note, icon: Icon, to, iconClass }) => {
  const content = (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
          {note ? <p className="mt-1 text-xs text-gray-500">{note}</p> : null}
        </div>
        <div className={`rounded-md p-2 ${iconClass}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );

  if (!to) return content;
  return <Link to={to}>{content}</Link>;
};

export const AttentionPanel = ({ attentionItems, attentionAccounts }) => (
  <Card title="Attention Required" className="lg:col-span-1">
    {attentionItems.length === 0 ? (
      <p className="text-sm text-green-700">No critical operational issues right now.</p>
    ) : (
      <div className="space-y-3">
        {attentionItems.map((item) => (
          <div key={item.id} className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2">
            <span className="text-sm text-red-800">{item.label}</span>
            <Badge variant="red" size="sm">{item.value}</Badge>
          </div>
        ))}
      </div>
    )}

    {attentionAccounts.length > 0 ? (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Top impacted accounts</p>
        <div className="space-y-2">
          {attentionAccounts.map((account) => (
            <Link
              key={account.id}
              to={`/accounts/${account.id}`}
              className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 hover:border-red-300"
            >
              <span className="text-sm font-medium text-gray-800">{account.name || account.phonenumber}</span>
              <Badge size="sm" variant={LIVE_STATUS_VARIANTS[account.live_status] || 'gray'}>
                {account.live_status || 'unknown'}
              </Badge>
            </Link>
          ))}
        </div>
      </div>
    ) : null}
  </Card>
);

export const ActiveProcessesPanel = ({ processes }) => (
  <Card title="Active Processes" className="lg:col-span-2">
    {processes.length === 0 ? (
      <p className="text-sm text-gray-500">No active processes.</p>
    ) : (
      <div className="space-y-3">
        {processes.slice(0, 8).map((process) => {
          const total = process.total_items || 0;
          const processed = process.processed_items || 0;
          const progress = total > 0 ? Math.round((processed / total) * 100) : 0;

          return (
            <div key={process.id} className="rounded-md border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {process.title || PROCESS_TYPE_LABELS[process.process_type] || process.process_type}
                  </p>
                  <p className="text-xs text-gray-500">Updated {formatTimeAgo(process.updated_at || process.created_at)}</p>
                </div>
                <Badge variant={PROCESS_STATUS_VARIANTS[process.status] || 'gray'} size="sm">
                  {process.status}
                </Badge>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-blue-600" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>{processed}/{total} processed</span>
                <span>{progress}%</span>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

export const AgentMessagesPanel = ({ messages }) => (
  <Card title="Recent Agent Messages" className="lg:col-span-2">
    {messages.length === 0 ? (
      <p className="text-sm text-gray-500">No tracked agent messages yet.</p>
    ) : (
      <div className="space-y-3">
        {messages.map((message) => (
          <div key={message.id} className="rounded-md border border-gray-200 p-3">
            <div className="mb-1 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <Link to={`/agents/${message.agent_id}`} className="truncate text-sm font-medium text-blue-600 hover:underline">
                  @{message.agent_username || 'unknown'}
                </Link>
                <span className="truncate text-xs text-gray-500">{message.project_name || '-'}</span>
              </div>
              <Badge size="sm" variant={PROCESS_STATUS_VARIANTS[message.chain_status] || 'gray'}>
                {message.chain_status || 'unknown'}
              </Badge>
            </div>
            <p className="line-clamp-2 text-sm text-gray-800">{message.message_text || '(empty message)'}</p>
            <p className="mt-1 text-xs text-gray-500">{formatTimeAgo(message.sent_at)}</p>
          </div>
        ))}
      </div>
    )}
    <div className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-500">
      Open <Link to="/agents" className="text-blue-600 hover:underline">Agents → Messages Log</Link> for full stream.
    </div>
  </Card>
);

export const QuickNavigationPanel = () => (
  <Card title="Quick Navigation" className="lg:col-span-1">
    <div className="grid grid-cols-1 gap-2">
      {[
        { to: '/accounts', label: 'Accounts', note: 'control + connection' },
        { to: '/sessions', label: 'Sessions', note: 'session files and recovery' },
        { to: '/processes', label: 'Processes', note: 'active and history' },
        { to: '/agents', label: 'Agents', note: 'directory and messages log' },
        { to: '/groups', label: 'Groups', note: 'discovery and quality' },
        { to: '/projects', label: 'Projects', note: 'routing and keywords' },
      ].map((item) => (
        <Link key={item.to} to={item.to} className="rounded-md border border-gray-200 px-3 py-2 hover:border-blue-300">
          <p className="text-sm font-medium text-gray-900">{item.label}</p>
          <p className="text-xs text-gray-500">{item.note}</p>
        </Link>
      ))}
    </div>
    <div className="mt-4 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
      <div className="mb-1 flex items-center gap-1 font-semibold">
        <ExclamationTriangleIcon className="h-4 w-4" />
        Operator note
      </div>
      Use this page for triage, then move to dedicated sections for actions.
    </div>
    <div className="mt-3 rounded-md bg-blue-50 p-3 text-xs text-blue-800">
      <div className="mb-1 flex items-center gap-1 font-semibold">
        <ChatBubbleLeftRightIcon className="h-4 w-4" />
        Agent stream
      </div>
      If feed is empty, agents have not produced tracked responses yet.
    </div>
  </Card>
);
